import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import React from "react";
import { Link } from "react-router-dom";
import * as uuid from "uuid";
import { ShiftReportsList, ShiftReportsRow } from "../../../api";
import {
    LoadingTableElement,
    NullTableElement,
} from "../../../components/table/nullTable";
import {
    HeadCell,
    SortableTable,
    newDisplayCond,
} from "../../../components/table/sortableTable";
import {
    convertFloatToPercentage,
    convertMinToDisplayFormat,
    convertToShiftDate,
    formatPaymentAmountDisplay,
} from "../../../services/common/convert";
import { getDriverById } from "../../../services/common/drivers";
import {
    resetBreakReports,
    selectBreakReports,
} from "../../../services/shift_monitoring/breaks/breaks_report";
import {
    resetTripReports,
    selectTripReports,
} from "../../../services/shift_monitoring/trips/trip_reports";
import * as settings from "../../../settings";
import { commonState, store } from "../../../store";
import { colors } from "../../../theme";
import { toInlineBatsuMark } from "../../../utils/formatting";
import { State } from "../store";

export interface Props {
    commonState: commonState;
    shifts: State;
}

const readTableState = () => {
    const state = store.getState();
    return state.shifts.tableSortState;
};

export const convertShiftReportsToDataTableFormat = (
    shiftReportsList: ShiftReportsList | null,
    dummyEnabled: boolean
): Row[] => {
    if (!shiftReportsList || !shiftReportsList.shiftReports) {
        return [];
    }

    const _data = shiftReportsList.shiftReports.map((rec: ShiftReportsRow) => {
        let officeName = rec.officeName;
        if (dummyEnabled) {
            officeName = rec.officeName
                ? rec.officeName
                : `${rec.officeId}営業所`;
        }

        const elmt = getDriverById(rec.driverId, rec.officeId);
        const drivername = elmt ? elmt.driverName : rec.driverName;

        const needGuidanceHermes = rec.needGuidanceHermes == "required" ? 1 : 0;

        const needGuidanceEarnings =
            rec.needGuidanceEarnings == "required" ? 1 : 0;

        const needGuidanceBreaks = rec.needGuidanceBreaks == "required" ? 1 : 0;
        return {
            shift_date: convertToShiftDate(rec.shiftDate),
            row_id: uuid.v4(),
            office: officeName,
            driver_id: rec.driverCode,
            driver_name: drivername,
            need_guidance_stop: needGuidanceBreaks,
            need_guidance_navi_usage: needGuidanceHermes,
            need_guidance_earnings: needGuidanceEarnings,
            shift_length: rec.shiftLengthMin,
            stop_length: rec.breaksLengthMin,
            pause_length: rec.pauseCarRequestLengthMin,
            navi_usage_rate: rec.naviUsageRate,
            hourly_earnings: rec.timeEarnings,
            payment_method: rec.paymentMethod,
            next_page_links: <LinkToNext selectedRecord={rec} />,
        } as Row;
    });
    return _data;
};

interface LinkProps {
    selectedRecord: ShiftReportsRow;
}

const LinkToNext: React.FC<LinkProps> = (props: LinkProps) => {
    const shift_date = props.selectedRecord.shiftDate;
    const office_id = props.selectedRecord.officeId;
    const driver_id = props.selectedRecord.driverId;
    const needGuidanceHeader = props.selectedRecord.needGuidanceHermes;
    const driver_code = props.selectedRecord.driverCode;

    return (
        <Box>
            {/* <Box> */}
            <MenuItem
                component={Link}
                sx={{
                    color: colors.blue.main,
                    p: 0.5,
                    pl: 3,
                    minHeight: "0px",
                }}
                onClick={async (x) => {
                    if (shift_date && office_id && driver_id) {
                        await resetBreakReports();
                        await selectBreakReports(
                            shift_date,
                            office_id,
                            driver_id
                        );
                    } else {
                        console.log(
                            "One of these was empty... shift_date, office_id, driver_id",
                            props.selectedRecord
                        );
                    }
                }}
                to="/driver_breaks"
            >
                停車詳細
            </MenuItem>

            {/* </Box>
            <Box> */}
            <MenuItem
                component={Link}
                sx={{
                    color: colors.blue.main,
                    p: 0.5,
                    pl: 3,
                    minHeight: "0px",
                }}
                onClick={async (x) => {
                    if (shift_date && office_id && driver_id) {
                        console.debug(
                            "Selected a shift:",
                            props.selectedRecord
                        );
                        await resetTripReports();

                        const trips = await selectTripReports(
                            shift_date,
                            office_id,
                            driver_id,
                            needGuidanceHeader,
                            driver_code,
                            props.selectedRecord.naviUsageRate
                        );
                    } else {
                        console.log(
                            "One of these was empty... shift_date, office_id, driver_id",
                            props.selectedRecord
                        );
                    }
                }}
                to="/driver_trips"
            >
                ナビ利用詳細
            </MenuItem>
            {/* </Box> */}
        </Box>
    );
};

export const ShiftSearchTable: React.FC<Props> = (props: Props) => {
    console.debug(
        "DriverUsage shifts...",
        props.shifts,
        props.commonState.driverNames
    );

    let lastSortedColumn = "shift_date";
    let sortDirection = "desc";
    const tableState = readTableState();

    if (tableState) {
        lastSortedColumn = tableState.lastSortedColumn;
        sortDirection = tableState.sortDirection;
    }

    const defaultSortAsc = sortDirection == "asc" ? true : false;

    console.debug(
        "sorted... will sort table by",
        lastSortedColumn,
        sortDirection,
        defaultSortAsc
    );

    const _data = convertShiftReportsToDataTableFormat(
        props.shifts.shiftReports,
        props.commonState.versions.dummy_enabled
    );

    const handleRowClick = (r) => {
        console.log(r);
    };

    if (!props.shifts.shiftReports) {
        return <LoadingTableElement />;
    }

    if (
        props.shifts.shiftReports.shiftReports &&
        props.shifts.shiftReports.shiftReports.length == 0
    ) {
        return <NullTableElement />;
    }
    return (
        <SortableTable
            tableHeaders={headCells}
            tableData={_data}
            tableProps={props}
            displayCond={newDisplayCond(lastSortedColumn, "asc", 0, 20)}
            rowIdField={"row_id"}
            onRowClickMethod={handleRowClick}
            slimRows={true}
        />
    );
};

const headCells: HeadCell[] = [
    {
        label: "乗務日",
        id: "shift_date",
        numeric: false,
        disablePadding: true,
        grow: 3,
    },
    {
        label: "営業所",
        id: "office",
        numeric: false,
        grow: 1,
        disablePadding: true,
        wrap: true,
    },
    {
        label: "乗務員番号",
        id: "driver_id",
        numeric: false,
        // align: "right",
        headerAlignRight: false,
        disablePadding: true,
        grow: 2,
        wrap: true,
    },
    {
        label: "乗務員氏名",
        id: "driver_name",
        numeric: false,
        grow: 2,
        wrap: true,
        disablePadding: true,
        cell: function DriverCell(r) {
            // 成果課金の乗務員の色を変える
            let style = { color: "inherit" };
            if (r.payment_method == 2) {
                style = { color: colors.green.main };
            }
            return <Box sx={style}>{r.driver_name}</Box>;
        },
    },
    {
        label: "乗務時間",
        id: "shift_length",
        align: "right",
        numeric: true,
        grow: 1,
        headerAlignRight: true,
        disablePadding: true,
        format: (x: number, row: any) => {
            return convertMinToDisplayFormat(x);
        },
    },
    {
        label: "停車時間",
        id: "stop_length",
        numeric: true,
        grow: 2,
        align: "right",
        disablePadding: true,
        headerAlignRight: true,
        format: (x: number, row: any) => {
            return toInlineBatsuMark(
                convertMinToDisplayFormat(row.stop_length),
                row.need_guidance_stop
            );
        },
    },
    {
        label: "配車停止時間",
        id: "pause_length",
        numeric: true,
        grow: 2,
        align: "right",
        disablePadding: true,
        headerAlignRight: true,
        format: (x: number, row: any) => {
            return convertMinToDisplayFormat(row.pause_length);
        },
    },
    {
        label: "ナビ利用率",
        id: "navi_usage_rate",
        numeric: true,
        align: "right",
        headerAlignRight: true,
        disablePadding: true,
        grow: 1,
        format: (x: number, row: any) => {
            if (x === settings.naviUsageBlankVal) {
                return <Box />;
            }
            return toInlineBatsuMark(
                convertFloatToPercentage(x),
                row.need_guidance_navi_usage
            );
        },
    },
    {
        label: "時間営収",
        id: "hourly_earnings",
        numeric: true,
        headerAlignRight: true,
        disablePadding: true,

        grow: 2,
        align: "right",
        format: (x: number, row: any) => {
            return toInlineBatsuMark(
                formatPaymentAmountDisplay(x),
                row.need_guidance_earnings,
                "円"
            );
        },
    },
    {
        label: "",
        id: "next_page_links",
        numeric: true,
        grow: 2,
    },
];

type Row = {
    shift_date?: string;
    office?: string;
    driver_id?: string;
    driver_name?: string;
    need_guidance_stop?: number;
    need_guidance_navi_usage?: number;
    need_guidance_earnings?: number;
    shift_length?: number;
    stop_length?: number;
    pause_length?: number;
    navi_usage_rate?: number;
    hourly_earnings?: number;
    next_page_links?: JSX.Element;
    payment_method?: number;
};
